import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { FormArray } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    assignModelOptions,
    CalibrationModel,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfirmationMeasurementToolsModel,
    ConnectivityService,
    EventService,
    IValueChanged,
    LoadingScreenComponent,
    LoadingScreenModel,
    MeasurementToolModel,
    RadioActiveModel,
    RadioModel,
    TextModel,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, finalize, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { CalibrationDateComponent } from '../../shared/components/calibration-date/calibration-date.component';
import { ConfirmationDataInstanceToken, ConfirmationDataInterface } from '../interfaces/confirmation-data.interface';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-confirmation-measurement-tools',
    templateUrl: './confirmation-measurement-tools.component.html',
    styleUrls: ['./confirmation-measurement-tools.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ConfirmationMeasurementToolsComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    public model: ConfirmationMeasurementToolsModel;

    @Input()
    public parentForm: FormArray;

    public showLegalCheckIEC62353Options = false;
    public showLegalCheckVDE701702Options = false;
    public showLegalCheckNEN1010Options = false;
    public isAnyLegalCheck3551SelectedYes = false;
    public showCalibrationDate = false;
    private measurementToolId: string;
    private toolIndex: number;
    public checkCalibrationDate$: Subject<void>;
    private destroy$ = new Subject<void>();

    public hasOpenAppNotifcation = false;
    private measurementToolModelsSubject: BehaviorSubject<MeasurementToolModel[]>;
    public measurementToolModels$: Observable<MeasurementToolModel[]>;
    public readonly faTrashAlt = faTrashAlt;
    public readonly calibrationValue: number | undefined;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(ConfirmationDataInstanceToken) private readonly confirmationService: ConfirmationDataInterface,
        private readonly modalService: BsModalService,
        private readonly connectivityService: ConnectivityService,
        private readonly eventService: EventService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.showCalibrationDate = this.configService.getShowCalibrationDate();
        this.calibrationValue = this.configService.getRadioactiveSurveyToolCalibration();
        this.measurementToolModelsSubject = new BehaviorSubject<MeasurementToolModel[]>([]);
        this.measurementToolModels$ = this.measurementToolModelsSubject.asObservable();

        this.measurementToolModels$
            .pipe(
                mergeMap((models) => {
                    if (models.length > 0) {
                        const unselectedEntries = this.model.getUnselectedTools();
                        this.model.setDropdownEntries(unselectedEntries);
                    }
                    return models;
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((model) => {
                this.setDropdownEntriesByModel(model);
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.model &&
            !changes.model.firstChange &&
            changes.model.previousValue?.measurementTools?.value !== changes.model.currentValue?.measurementTools?.value
        ) {
            this.measurementToolModelsSubject.next(this.model.measurementTools.value);
            this.showMeasurementOptions();
        }
    }

    public ngOnInit(): void {
        this.onLegalCheckOptionChange();

        if (this.showCalibrationDate) {
            this.checkCalibrationDate$ = new Subject<void>();
            this.checkCalibrationDate$
                .asObservable()
                .pipe(
                    exhaustMap(() => {
                        const loadingScreenModel = new LoadingScreenModel(
                            <string>(
                                this.translate.instant(
                                    'CONFIRMATION_MEASUREMENT_TOOLS_CALIBRATION_DATE_LOADING_MESSAGE',
                                )
                            ),
                        );
                        const submitModalInstance = this.modalService.show(LoadingScreenComponent, {
                            class: 'modal-sm',
                            initialState: {
                                model: loadingScreenModel,
                            },
                            ariaLabelledBy: 'loading-screen-modal-title',
                        });
                        return loadingScreenModel.loadingScreenDisplayed$.pipe(
                            mergeMap(() => this.confirmationService.getCalibrationDate(this.measurementToolId)),
                            tap((data: CalibrationModel) => {
                                if (data.errorMessage) {
                                    this.log.error(
                                        'ConfirmationMeasurementToolsComponent: getCalibrationDate: Error:',
                                        data.errorMessage,
                                    );
                                    this.appNotificationService.setAppNotification(
                                        AppNotificationType.ERROR,
                                        <string>(
                                            this.translate.instant(
                                                'CONFIRMATION_MEASUREMENT_TOOLS_CALLIBRATION_DATE_ERROR_TITLE',
                                            )
                                        ),
                                        data.errorMessage,
                                    );
                                } else {
                                    const measurementTool = this.model.measurementTools.value[this.toolIndex];
                                    measurementTool.measurementToolId = assignModelOptions(
                                        measurementTool.measurementToolId,
                                        {
                                            ...measurementTool.measurementToolId.options,
                                            readonly: true,
                                        },
                                        measurementTool,
                                    );
                                    this.model.measurementTools.value[this.toolIndex].calibration.toolDescription =
                                        data.toolDescription;
                                    this.model.measurementTools.value[this.toolIndex].calibration.calibrationDate =
                                        data.calibrationDate
                                            ? moment(data.calibrationDate).format(
                                                  ConfigConstants.formatPatternDateGeneric,
                                              )
                                            : '';
                                    this.modalService.show(CalibrationDateComponent, {
                                        backdrop: 'static',
                                        class: 'modal-md',
                                        initialState: {
                                            model: this.model.measurementTools.value[this.toolIndex],
                                        },
                                        ariaLabelledBy: 'calibration-date-modal-title',
                                    });
                                    this.changeDetector.detectChanges();
                                }
                            }),
                            catchError((error) => {
                                this.log.error(
                                    'ConfirmationMeasurementToolsComponent: getCalibrationDate: Error:',
                                    error,
                                );
                                this.appNotificationService.setAppNotification(
                                    AppNotificationType.ERROR,
                                    <string>(
                                        this.translate.instant(
                                            'CONFIRMATION_MEASUREMENT_TOOLS_CALLIBRATION_DATE_ERROR_TITLE',
                                        )
                                    ),
                                    `${<string>(
                                        this.translate.instant(
                                            'CONFIRMATION_MEASUREMENT_TOOLS_CALLIBRATION_DATE_ERROR_MESSAGE',
                                        )
                                    )} ${JSON.stringify(error)}`,
                                );
                                return of(undefined);
                            }),
                            finalize(() => submitModalInstance.hide()),
                            takeUntil(this.destroy$),
                        );
                    }),
                )
                .subscribe();
        }

        // if user selects Phone Fix in time recording screen,
        // then by default, 'No measurement tools used' check box must be selected.
        // https://evorait.atlassian.net/browse/SHCNGMP58-2391

        if (!this.configService.getEnableRemoteWork()) {
            this.eventService.phoneFixSelected$
                .pipe(
                    tap((isPhoneFixSelected) => {
                        this.model.noMeasurementTool.model.value = isPhoneFixSelected;
                        this.onNoMeasurementToolsUsedChange();
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe();
        }
        if (this.model.showMeasurementToolsDetails) {
            // trigger dropdown list filtering and behavior
            this.measurementToolModelsSubject.next(this.model.measurementTools.value);
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    /**
     * set on each dropdown model filtered list of net yet selected values
     *
     * @param model
     */
    private setDropdownEntriesByModel(model: MeasurementToolModel): void {
        const unselectedEntries = this.model.getUnSelectedDropdownEntries();
        model.setDropdownEntries(unselectedEntries);

        const showDropdown = model.dropdownValues.value.length > 0;
        model.setDropdownVisibility(model.technicalId.value ? !!model.dropdownValues.selectedEntry : showDropdown);
    }

    public addNewRadioActiveTool(): void {
        this.model.radioActiveModelList.push(new RadioActiveModel());
    }

    public deleteRadioActiveTool(index: number): void {
        this.model.radioActiveModelList.splice(index, 1);
    }

    public onDropdownChange(toolModel: MeasurementToolModel): void {
        toolModel.updateFields();
        this.measurementToolModelsSubject.next(this.model.measurementTools.value);
        this.updateFormValidity();
    }

    public onTechnicalIdChange(toolModel: MeasurementToolModel, index: number, event: IValueChanged): void {
        const previousEntry = toolModel.dropdownValues.selectedEntry
            ? toolModel.dropdownValues.selectedEntry.model.value.trim().toLocaleLowerCase()
            : event.oldValue?.trim().toLocaleLowerCase();
        const currentEntry = toolModel.technicalId.value.trim().toLocaleLowerCase();

        this.checkForDuplicateTechId(toolModel, index, currentEntry);

        // If user add just space Example: User enters 'ABCD' changes to 'ABCD  ' which is same, then terminate the function
        if (previousEntry === currentEntry) {
            return;
        }

        const selectedIndex = toolModel.dropdownValues.value.findIndex(
            (entry) => entry.model.value.toLocaleLowerCase() === currentEntry,
        );

        if (selectedIndex >= 0) {
            toolModel.selectToolByIndex(selectedIndex);
            this.measurementToolModelsSubject.next(this.model.measurementTools.value);
        } else {
            if (toolModel.dropdownValues.selectedEntry) {
                toolModel.dropdownValues.deselect();
                toolModel.updateFields();
                this.measurementToolModelsSubject.next(this.model.measurementTools.value);
            }
            toolModel.setDropdownVisibility(!toolModel.technicalId.value && toolModel.dropdownValues.value.length > 0);
        }
        this.updateFormValidity();
    }

    /**
     * This function is triggered on Blur event to check if the technical id is duplicate
     * If TechId is duplicate then a pop-up is displayed with message "abc123  is already selected" and clear's techId field
     *
     * @param toolModel the tool model
     * @param index the index of the tool
     * @param currentEntry the current entry
     */
    public checkForDuplicateTechId(toolModel: MeasurementToolModel, index: number, currentEntry: string): void {
        const isDuplicateTechId = this.model.technicalIdAlreadyAvailable(currentEntry, index);

        if (isDuplicateTechId && currentEntry && !this.hasOpenAppNotifcation) {
            // prevent openeing same modal twice
            this.hasOpenAppNotifcation = true;
            this.log.debug(
                `ConfirmationMeasurementToolsComponent: onTechnicalIdChange: '${toolModel.technicalId.value}' already exist`,
            );
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_LABEL'),
                <string>this.translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_VALIDATION_ERROR_MESSAGE', {
                    technicalId: toolModel.technicalId.value,
                }),
                () => {
                    this.hasOpenAppNotifcation = false;
                },
            );
            toolModel.technicalId.value = '';
            toolModel.technicalId = _.cloneDeep(toolModel.technicalId);
            toolModel.setDropdownVisibility(toolModel.dropdownValues.value.length > 0);
        }
    }

    /**
     * Add new measuring tool row
     */
    public addNewTool(): void {
        this.model.addNewMeasurementToolModel();
        this.measurementToolModelsSubject.next(this.model.measurementTools.value);
    }

    /**
     * Delete the existing measuring tool row
     * If a row is deleted, If a tool is selected in dropdown we have to add the deleted tool to all dropdown
     *
     * @param index the index of the tool
     */
    public deleteTool(index: number): void {
        this.model.measurementTools.removeModel(index);
        this.measurementToolModelsSubject.next(this.model.measurementTools.value);
        this.updateFormValidity();
    }

    public resetRequiredOption(): void {
        this.model.resetRequiredOption();
        this.updateFormValidity();
    }

    public setOther3551legalChecksToNo(model: RadioModel<TextModel>): void {
        if (
            (model === this.model.legalCheck3551PermanentlyConnected &&
                this.model.legalCheck3551PermanentlyConnected.selectedEntry.value === ConfigConstants.optionValueYes) ||
            (model === this.model.legalCheck3551NonDetachablePowerCord &&
                this.model.legalCheck3551NonDetachablePowerCord.selectedEntry.value === ConfigConstants.optionValueYes)
        ) {
            this.model.legalCheck3551DetachablePowerCord.selectByPredicate(
                (entry) => entry.value === ConfigConstants.optionValueNo,
            );
        }
        if (
            (model === this.model.legalCheck3551DetachablePowerCord &&
                this.model.legalCheck3551DetachablePowerCord.selectedEntry.value === ConfigConstants.optionValueYes) ||
            (model === this.model.legalCheck3551NonDetachablePowerCord &&
                this.model.legalCheck3551NonDetachablePowerCord.selectedEntry.value === ConfigConstants.optionValueYes)
        ) {
            this.model.legalCheck3551PermanentlyConnected.selectByPredicate(
                (entry) => entry.value === ConfigConstants.optionValueNo,
            );
        }
        if (
            (model === this.model.legalCheck3551DetachablePowerCord &&
                this.model.legalCheck3551DetachablePowerCord.selectedEntry.value === ConfigConstants.optionValueYes) ||
            (model === this.model.legalCheck3551PermanentlyConnected &&
                this.model.legalCheck3551PermanentlyConnected.selectedEntry.value === ConfigConstants.optionValueYes)
        ) {
            this.model.legalCheck3551NonDetachablePowerCord.selectByPredicate(
                (entry) => entry.value === ConfigConstants.optionValueNo,
            );
        }

        this.onLegalCheckOptionChange();
        this.updateFormValidity();
    }

    public updateFormValidity(shouldEmitEvent = false): void {
        this.parentForm.controls.forEach((control) => {
            control.updateValueAndValidity({ emitEvent: shouldEmitEvent });
        });
        // need to detectChanges, otherwise the component will not update.
        this.changeDetector.detectChanges();
    }

    public onNoMeasurementToolsUsedChange(): void {
        const isMeasurementToolsSectionExpanded = !this.model.noMeasurementTool.model.value;

        if (isMeasurementToolsSectionExpanded) {
            this.measurementToolModelsSubject.next(this.model.measurementTools.value);
        } else {
            this.model.measurementTools.value.forEach((tool) => {
                tool.dropdownValues.deselect();
            });
            this.model.measurementTools.value = [];
            this.model.revertDropdownValues();
            this.addNewTool();

            // If user checks  "No measurement tool selected", we reset all tools value and
            // If user unchecks "We add only those Radioactive tool which are coming from backend"
            this.model.setRadioActiveSurvey(this.model.radioActiveToolsFromBackend);
        }

        this.updateFormValidity();
    }

    public showMeasurementOptions(): void {
        if (this.model.showIEC62353) {
            this.showLegalCheckIEC62353Options =
                this.model.legalCheckIEC62353.selectedEntry.value === ConfigConstants.optionValueYes;
        }
        if (this.model.showVDE701702) {
            this.showLegalCheckVDE701702Options =
                this.model.legalCheckVDE701702.selectedEntry.value === ConfigConstants.optionValueYes;
        }
        if (this.model.showNEN1010) {
            this.showLegalCheckNEN1010Options =
                this.model.legalCheckNEN1010.selectedEntry.value === ConfigConstants.optionValueYes;
        }
        if (this.model.showLegalCheck3551) {
            this.isAnyLegalCheck3551SelectedYes =
                this.model.legalCheck3551PermanentlyConnected?.selectedEntry?.value ===
                    ConfigConstants.optionValueYes ||
                this.model.legalCheck3551DetachablePowerCord?.selectedEntry?.value === ConfigConstants.optionValueYes ||
                this.model.legalCheck3551NonDetachablePowerCord?.selectedEntry?.value ===
                    ConfigConstants.optionValueYes;
        }
    }

    /**
     * If a Legal check was selected then it is mandatory to fill out the measurement tool
     * measurement tool should be mandatory if any one of following is true
     * 1. legalCheckIEC62353 checkbox is checked
     * 2. legalCheckVDE701702 checkbox is checked
     * 3. legalCheckNEN1010 checkbox is checked and legalCheckNEN1010Radio "Measurement performed" option is selected
     * 4. legalCheckRadioProtection (radiation protection) checkbox is selected
     */
    public onLegalCheckOptionChange(): void {
        this.showMeasurementOptions();

        const legalCheckValue =
            (this.model.showIEC62353 && this.showLegalCheckIEC62353Options) ||
            (this.model.showVDE701702 && this.showLegalCheckVDE701702Options) ||
            (this.model.showRadioProtection && this.model.legalCheckRadioProtection.value) ||
            (this.model.showNEN1010 &&
                this.showLegalCheckNEN1010Options &&
                this.model.legalCheckNEN1010.value &&
                this.model.legalCheckNEN1010Radio.selectedEntry.name ===
                    this.model.configLegalCheckMeasurementPerformedCode) ||
            (this.model.showLegalCheck3551 && this.isAnyLegalCheck3551SelectedYes);

        this.model.noMeasurementTool = assignModelOptions(
            this.model.noMeasurementTool,
            {
                ...this.model.noMeasurementTool.model.options,
                disabled: legalCheckValue,
            },
            this.model,
        );

        if (this.model.noMeasurementTool.model.options.disabled) {
            this.model.noMeasurementTool.model.value = false;
        }
        this.resetRequiredOption();
        // change detection is required here because as of now form does not add/remove the measurement controls that are
        // dependent on legalCheckValue (true/false)
        // So calling updateFormValidity() here will not take into account the addition/removal of extra controls
        // and hence form validity value will be wrong
        this.updateFormValidity();
    }

    public validateForm(): void {
        this.changeDetector.detectChanges();
    }

    public getCalibrationDate(toolId: string, toolIndex: number): void {
        this.measurementToolId = toolId;
        this.toolIndex = toolIndex;
        if (!this.connectivityService.isInternetConnectionAvailable()) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_CALLIBRATION_DATE_ERROR_TITLE'),
                <string>this.translate.instant('CONFIRMATION_MEASUREMENT_TOOLS_CALLIBRATION_DATE_ERROR_OFFLINE_MODE'),
            );
        } else {
            this.checkCalibrationDate$.next();
        }
    }

    public trackByMeasurementToolId(index: number, item: MeasurementToolModel): string {
        return item.measurementToolKey;
    }
}
